import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement } from 'react';
import { renderChildren } from './renderChildren';
import { resolveRichTextElementMetadata } from './resolveRichTextElementMetadata';
import { RichTextRendererError } from './RichTextRendererError';
import { useRichTextRenderMetadata } from './RichTextRenderMetadataContext';
const getElementDataAttributes = (element, attributeNamePrefix = 'contember-') => {
    const { children, referenceId, ...extendedSpecifics } = element;
    return Object.fromEntries(Object.entries(extendedSpecifics)
        .filter(([, value]) => {
        const t = typeof value;
        return t === 'string' || t === 'number' || t === 'boolean';
    })
        .map(([attribute, value]) => [`data-${attributeNamePrefix}${attribute.toLowerCase()}`, value]));
};
export function RenderElementFallback({ element, children, options }) {
    const attributes = getElementDataAttributes(element, options.attributeNamePrefix);
    switch (element.type) {
        case 'anchor':
            return (_jsx("a", Object.assign({}, attributes, { href: element.href }, { children: children }), void 0));
        case 'heading':
            return createElement(`h${element.level}`, attributes, children); // TODO numbered
        case 'horizontalRule':
            return _jsx("hr", Object.assign({}, attributes), void 0);
        case 'listItem':
            return _jsx("li", Object.assign({}, attributes, { children: children }), void 0);
        case 'orderedList':
            return _jsx("ol", Object.assign({}, attributes, { children: children }), void 0);
        case 'paragraph':
            return _jsx("p", Object.assign({}, attributes, { children: children }), void 0);
        case 'reference': {
            return _jsx(ReferenceElementFallback, { element: element, children: children }, void 0);
        }
        case 'scrollTarget':
            return (_jsx("span", Object.assign({}, attributes, { id: element.identifier }, { children: children }), void 0));
        case 'table': {
            const firstRow = element.children[0];
            if (!firstRow || firstRow.headerScope !== 'table') {
                return (_jsx("table", Object.assign({}, attributes, { children: _jsx("tbody", { children: children }, void 0) }), void 0));
            }
            return (_jsxs("table", Object.assign({}, attributes, { children: [_jsx("thead", { children: renderChildren([firstRow], options) }, void 0),
                    _jsx("tbody", { children: renderChildren(element.children.slice(1), options) }, void 0)] }), void 0));
        }
        case 'tableRow':
            return _jsx("tr", Object.assign({}, attributes, { children: children }), void 0);
        case 'tableCell': {
            if (element.headerScope) {
                return (_jsx("th", Object.assign({}, attributes, { scope: element.headerScope }, { children: children }), void 0));
            }
            return _jsx("td", Object.assign({}, attributes, { children: children }), void 0);
        }
        case 'unorderedList':
            return _jsx("ul", Object.assign({}, attributes, { children: children }), void 0);
        default: {
            if (__DEV_MODE__) {
                throw new RichTextRendererError(`RichTextRenderer: unknown element of type '${element.type}'.`);
            }
            else {
                return children; // At least render the text contents
            }
        }
    }
}
function ReferenceElementFallback({ element, children }) {
    var _a;
    const metadata = useRichTextRenderMetadata();
    const elementMetadata = resolveRichTextElementMetadata(element, metadata);
    if (elementMetadata.referenceRenderer === undefined ||
        elementMetadata.referenceType === undefined ||
        elementMetadata.reference === undefined) {
        if (__DEV_MODE__) {
            throw new RichTextRendererError(`RichTextRenderer: cannot render reference of type '${elementMetadata.referenceType}'.`);
        }
        else {
            return children; // At least render the text contents
        }
    }
    return (_jsx(_Fragment, { children: (_a = elementMetadata.referenceRenderer) === null || _a === void 0 ? void 0 : _a.call(elementMetadata, {
            referenceType: elementMetadata.referenceType,
            referenceRenderer: elementMetadata.referenceRenderer,
            reference: elementMetadata.reference,
            formatVersion: elementMetadata.formatVersion,
            element,
            children,
        }) }, void 0));
}
//# sourceMappingURL=RenderElementFallback.js.map